"use client";

import useSWRInfinite from 'swr/infinite';
import { useState, useEffect, use } from 'react';
import { Skeleton, message, Spin, Button } from 'antd';
import { EventCard } from "./EventCard"
import { MarketCard } from "./MarketCard"
import { MatchCard } from "./MatchCard"
import { debounce } from "@/utils/tools";
import { getEventsByTag, getRecommendEvents } from "@/service/index/index";

export type CardListProps = {
  tagId: string | number,
  isMobile: boolean
};
export type EventDataType = {
  event: {
    title: string,
    endDate: string,
    image: string,
    labels: string[],
    eventLink: string,
    insight?: string,
    matchMarkets: {
      "defaultOutcome": string,
      "isAiPrediction": number,
      "isWin": number,
      "marketId": number,
      "marketStatus": string,
      "price": number,
      "score": number | null,
      "teamLogo": string,
      "teamName": string
    }[],
    markets: {
      aiPrediction: string,
      chanceDisplay: string,
      id: number,
      image: string,
      itemTitle: string,
      outcomeName1: string,
      outcomeName2: string,
      outcomePrice1: number,
      outcomePrice2: number,
      question: string
    }[],
    "matchStartTime": string,
    "matchStartTimestamp": number,
    "matchState": string,
    "series": string,
  },
  priceTrend: {
    label: string,
    series: {
      data: number[],
      name: string
    }[],
    type: string,
    xAxis: string[]
    yAxisTicks: { name: string, value: number }[]
  },
  market: {
    eventLink: string,
    eventId: number,
    marketId: number,
    image: string,
    aiPrediction: string,
    outcomeName1: string,
    outcomeName2: string,
    outcomePrice1: number,
    outcomePrice2: number,
    question: string
  },
  type: string
}
export const CardList: React.FC<CardListProps> = ({ tagId, isMobile, ...props }) => {

  const pageSize = 20;
  const fetcher = async (key: any) => {
    // key 是 getKey 函数返回的值，例如 { page: 1, pageSize: 10 }
    // const response = await getAllOrders(key); // 直接将 key 作为参数传递给 getAllOrders
    let response;
    if (tagId === '-1') {
      response = await getRecommendEvents<EventDataType[]>(key)
    } else {
      response = await getEventsByTag<EventDataType[]>(tagId + '', key)
    }
    // 检查 HTTP 状态码
    if (response.status !== "SUCCESS") {
      message.error(response?.message || 'Error fetching data');
      return;
    }
    return response.data; // 确保返回数据
  };

  const getKey = (pageIndex: number, previousPageData: any) => {
    // 如果前一页数据不存在，或者前一页数据条数小于 pageSize，说明没有更多数据了
    if (previousPageData && (!previousPageData || previousPageData?.length < pageSize)) {
      return null;
    }

    // 返回下一页的请求参数
    return {
      page: pageIndex + 1, // 你的 API 可能从第 1 页开始
      size: pageSize, // 每页数量，根据你的 API 调整
      // status: status, // 其他可能的过滤参数
    };
  };


  // 使用 useSWRInfinite 替换原有的 useState 和 useEffect 逻辑
  const {
    data,         // 数据数组，每个元素为一页的数据
    error,
    size,         // 当前加载的页数
    setSize,      // 设置加载页数的函数
    isLoading,    // 首次加载状态
    isValidating, // 任何页数的数据验证（加载）状态
    mutate,     // 重新验证数据的函数
  } = useSWRInfinite(getKey, fetcher,
    {
      // 可选配置项，例如：
      revalidateFirstPage: false, // 避免重新验证时重复请求第一页
      parallel: false, // 允许并行请求多个页面,
      revalidateOnFocus: false, // 根据需求调整
      shouldRetryOnError: true,
      errorRetryCount: 3,
    }
  );

  const orders = data ? data.flat() : []

  // if(!data) {
  //   return null
  // }

  // 计算所有已加载的数据项（扁平化处理）
  // const orders = data && data[0]?.length ? data[0] : [];

  // 判断是否还有更多数据可以加载
  // 假设你的接口返回的每页数据条数固定为 pageSize，如果某页返回的数据少于 pageSize，则认为没有更多数据
  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isReachingEnd = data && data.length > 0 && (data[data.length - 1]?.length || 0) < pageSize;
  const isEmpty = data?.[0]?.length === 0;

  // 加载下一页的函数
  const loadMore = () => {
    if (!isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  };

  const handleScroll = debounce((e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = globalThis.window.document.body;
    const threshold = 100; // 距离底部 50px 时触发加载
    if (
      scrollHeight - scrollTop <= clientHeight + threshold && !isLoadingMore &&
      !isReachingEnd
    ) {
      loadMore();
    }
  }, 500);

  useEffect(() => {
    mutate()
  }, [tagId]);


  useEffect(() => {
    globalThis.window.document.body.addEventListener('scroll', handleScroll);

    return () => {
      globalThis.window.document.body.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div className={isMobile ? "cardList pb-20" : "grid-cols-3 grid gap-20"}>
      {
        orders && orders.length != 0 && orders.map((item, idx) => {

          if (!item) return null

          return item.type === 'event' ? <EventCard key={idx} data={item} /> :
            (item.type === 'market' ? <MarketCard key={idx} data={item} /> :
              (item.type === 'match' ? <MatchCard key={idx} data={item} /> : null))
        })
      }

      {/* 加载更多状态提示 */}
      {isLoadingMore && (isMobile ?
        <div>
          <div className='cardItem_race pt-20'>
            <Skeleton active />
          </div>
          <div className='cardItem_race pt-20'>
            <Skeleton active />
          </div>
          <div className='cardItem_race pt-20'>
            <Skeleton active />
          </div>
        </div>
        :
        <>
          <div className='cardItem_race pt-20'>
            <Skeleton active />
          </div>
          <div className='cardItem_race pt-20'>
            <Skeleton active />
          </div>
          <div className='cardItem_race pt-20'>
            <Skeleton active />
          </div>
        </>
      )}

      {/* 没有更多数据提示 */}
      {isReachingEnd && orders.length > 0 && (
        <div className="text-center py-100 text-bravve-200 text-12">
          No more data!
        </div>
      )}

      {/* 错误提示 */}
      {error && (
        <div className="text-center py-100 text-red-500">
          Failed to load: {error?.message}
          <Button type="primary" size="small" onClick={() => setSize(size)} className="ml-2 w-200">
            Retry
          </Button>
        </div>
      )}

      {/* 空状态 */}
      {!isLoading && isEmpty && (
        <div className="text-center py-100 text-bravve-200 text-12">
          No data yet.
        </div>
      )}
    </div>
  );
}
